import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePlans } from '../hooks/usePlans';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { ref, get, set, query, orderByChild, equalTo } from 'firebase/database';
import PaymentGateway from '../components/PaymentGateway';
import SEO from '../components/SEO';
import { apiFetch } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */
interface BotRecord {
  id: string;
  businessName: string;
  phoneNumberId: string;
}

interface UserData {
  name: string;
  email: string;
  phone: string;
  plan: string;
  onboardingCompleted: boolean;
  paymentStatus?: 'active' | 'failed' | 'pending' | 'pending_renewal' | 'cancelled' | 'expired' | 'trial';
  lastPaymentDate?: number;
  nextBillingDate?: number;
  stripeCustomerId?: string;
  stripePaymentMethodId?: string;
  failedPaymentAttempts?: number;
  cancelAtPeriodEnd?: boolean;
  subscriptionStatus?: string;
  billingCycle?: string;
  cardLast4?: string;
  cardBrand?: string;
  cardExpiry?: string;
  trialPlan?: string;
  trialStartedAt?: number;
  trialEndsAt?: number;
  trialUsed?: boolean;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  botId?: string;
  businessName?: string;
  description: string;
  amount: number;
  status: 'unpaid' | 'paid';
  type: 'setup_fee' | 'subscription' | 'subscription_renewal';
  contactEmail?: string;
  customerName?: string;
  customerEmail?: string;
  createdAt: number;
  dueDate?: number;
  paidAt?: number | null;
  transactionId?: string | null;
  paymentMethod?: string | null;
  plan?: string;
  date?: number;
}

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const companyName = process.env.REACT_APP_COMPANY_NAME || 'autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw';
const API_BASE = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');

// Plans are loaded dynamically from /api/plans via usePlans hook — see below

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const S = {
  page: { display: 'flex', minHeight: 'calc(100vh - 64px)', background: 'var(--bg-page)', fontFamily: "'DM Sans', system-ui, sans-serif" } as React.CSSProperties,
  sidebar: (c: boolean): React.CSSProperties => ({ width: c ? '72px' : '240px', background: 'var(--bg-sidebar)', borderRight: '1px solid var(--border-subtle)', transition: 'width 0.3s cubic-bezier(.4,0,.2,1)', display: 'flex', flexDirection: 'column', position: 'relative', flexShrink: 0 }),
  main: { flex: 1, overflow: 'auto', padding: '40px 32px' } as React.CSSProperties,
};

/* ─────────────────────────────────────────────
   TRIAL CARD ELEMENT (Stripe Elements)
───────────────────────────────────────────── */
function TrialCardElement() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
    const win = window as any;
    const stripe = win.Stripe?.(stripeKey);
    if (!stripe || !containerRef.current) return;

    const elements = stripe.elements();
    const card = elements.create('card', {
      style: {
        base: { color: 'var(--text-primary)', fontSize: '15px', fontFamily: "'DM Sans', system-ui, sans-serif", '::placeholder': { color: 'var(--text-faint)' }, backgroundColor: 'transparent' },
        invalid: { color: '#f87171' },
      },
    });
    card.mount(containerRef.current);
    win.__trialCardElement = card;
    return () => { card.destroy(); delete win.__trialCardElement; };
  }, []);

  return (
    <div ref={containerRef} style={{ background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '10px', padding: '14px 16px', minHeight: '46px' }} />
  );
}

/* ─────────────────────────────────────────────
   OUTSTANDING INVOICE BANNER
───────────────────────────────────────────── */
function OutstandingInvoiceBanner({ invoice, onPay }: { invoice: Invoice; onPay: () => void }) {
  const daysUntilDue = invoice.dueDate ? Math.ceil((invoice.dueDate - Date.now()) / (1000 * 60 * 60 * 24)) : null;
  const overdue = daysUntilDue !== null && daysUntilDue < 0;

  return (
    <div style={{ background: overdue ? '#dc26260d' : '#f59e0b0d', border: `1px solid ${overdue ? '#dc262633' : '#f59e0b33'}`, borderLeft: `4px solid ${overdue ? '#dc2626' : '#f59e0b'}`, borderRadius: '14px', padding: '20px 24px', marginBottom: '28px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '28px', flexShrink: 0 }}>{overdue ? '⚠️' : '🧾'}</span>
          <div>
            <div style={{ color: overdue ? '#f87171' : '#fbbf24', fontWeight: '700', fontSize: '15px', marginBottom: '4px' }}>
              {overdue ? 'Overdue Invoice' : 'Outstanding Invoice'} — ${invoice.amount}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
              {invoice.invoiceNumber} · {invoice.description}
            </div>
            {invoice.businessName && (
              <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginBottom: '4px' }}>Bot: {invoice.businessName}</div>
            )}
            {daysUntilDue !== null && (
              <div style={{ fontSize: '12px', color: overdue ? '#f87171' : '#fb923c', fontWeight: '500' }}>
                {overdue ? `Overdue by ${Math.abs(daysUntilDue)} day${Math.abs(daysUntilDue) !== 1 ? 's' : ''}` : `Due in ${daysUntilDue} day${daysUntilDue !== 1 ? 's' : ''}`}
              </div>
            )}
            <div style={{ marginTop: '8px', padding: '8px 12px', background: overdue ? '#dc262615' : '#f59e0b12', border: `1px solid ${overdue ? '#dc262630' : '#f59e0b30'}`, borderRadius: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              ⚡ Your bot status is <strong style={{ color: 'var(--text-primary)' }}>pending payment</strong>. It will activate immediately after this invoice is paid.
            </div>
          </div>
        </div>
        <button
          onClick={onPay}
          style={{ padding: '12px 28px', background: overdue ? 'linear-gradient(135deg,#dc2626,#b91c1c)' : 'linear-gradient(135deg,#f59e0b,#d97706)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          Pay Invoice ${invoice.amount}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   INVOICE PAY MODAL
───────────────────────────────────────────── */
function InvoicePayModal({ invoice, user, userData, onSuccess, onClose }: { invoice: Invoice; user: any; userData: UserData | null; onSuccess: () => void; onClose: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '20px', padding: '32px', maxWidth: '520px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ color: 'var(--text-primary)', fontWeight: '700', fontSize: '18px' }}>Pay Setup Invoice</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px', padding: '4px' }}>✕</button>
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>One-time fee — no recurring charges</div>
        </div>

        {/* Invoice summary */}
        <div style={{ background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '12px', padding: '18px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Invoice Summary</div>
            <div style={{ background: '#f59e0b22', color: '#f59e0b', fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '20px' }}>UNPAID</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Invoice #</span>
            <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '500' }}>{invoice.invoiceNumber}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Service</span>
            <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '500', maxWidth: '280px', textAlign: 'right' }}>{invoice.description}</span>
          </div>
          {invoice.businessName && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Business</span>
              <span style={{ color: 'var(--text-primary)', fontSize: '13px' }}>{invoice.businessName}</span>
            </div>
          )}
          <div style={{ borderTop: '1px solid var(--bg-element)', marginTop: '12px', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '15px' }}>Total</span>
            <span style={{ color: '#5B4FE8', fontWeight: '700', fontSize: '22px' }}>${invoice.amount}</span>
          </div>
        </div>

        {/* Payment gateway */}
        <PaymentGateway
          amount={invoice.amount}
          plan="setup"
          billingCycle="monthly"
          email={user?.email}
          phone={userData?.phone}
          userId={user?.uid}
          invoiceId={invoice.id}
          savedCardLast4={userData?.cardLast4}
          savedCardBrand={userData?.cardBrand}
          savedCardExpiry={userData?.cardExpiry}
          stripePaymentMethodId={userData?.stripePaymentMethodId}
          stripeCustomerId={userData?.stripeCustomerId}
          onSuccess={onSuccess}
          onError={(err: string) => alert('Payment failed: ' + err)}
        />

        <button onClick={onClose} style={{ width: '100%', marginTop: '12px', padding: '11px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--card-border)', borderRadius: '9px', cursor: 'pointer', fontWeight: '500', fontSize: '14px' }}>
          Cancel — pay later
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function Billing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, toggle } = useTheme();

  /* dynamic plan data from server */
  const { plans: dynamicPlans } = usePlans();
  const currentPlansForCycle = (cycle: 'monthly' | 'yearly') =>
    dynamicPlans.filter(p => !p.contactOnly).map(p => ({
      id: p.id, name: p.name, price: p[cycle] ?? 0,
      description: cycle === 'yearly' && p.id !== 'free' ? 'Save ~20%' : p.description,
      features: p.features, icon: p.icon, color: p.color, popular: p.popular,
    }));

  const [user, setUser] = useState<any>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [bots, setBots] = useState<BotRecord[]>([]);
  const [selectedPlan, setSelectedPlan] = useState('business');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [nextBillingDate, setNextBillingDate] = useState<Date | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string>('active');
  const [failedAttempts, setFailedAttempts] = useState<number>(0);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  const [paynowVerifying, setPaynowVerifying] = useState(false);

  /* modal state */
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [cardOnlyMode, setCardOnlyMode] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialBillingCycle, setTrialBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialError, setTrialError] = useState('');
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [entForm, setEntForm] = useState({ name: '', email: '', company: '', size: '', message: '' });
  const [entSent, setEntSent] = useState(false);
  const [entLoading, setEntLoading] = useState(false);

  /* coupon state — Paynow-only, see modules/coupons.js */
  const [couponInput, setCouponInput] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discountAmount: number; finalAmount: number } | null>(null);
  const [pendingInvite, setPendingInvite] = useState<{ code: string; proposedPlan: string; proposedBillingCycle: string } | null>(null);

  /* outstanding invoice from URL param */
  const invoiceIdFromUrl = searchParams.get('invoiceId');

  /* coupon pre-fill from a "send coupon" link (?coupon=CODE&plan=X&cycle=Y) */
  useEffect(() => {
    const coupon = searchParams.get('coupon');
    const planParam = searchParams.get('plan');
    const cycleParam = searchParams.get('cycle');
    if (coupon) setCouponInput(coupon);
    if (planParam) setSelectedPlan(planParam);
    if (cycleParam === 'yearly' || cycleParam === 'monthly') setBillingCycle(cycleParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* a coupon sent to this account that hasn't been redeemed yet */
  useEffect(() => {
    if (!user) return;
    apiFetch('/api/coupons/pending-invite').then(r => r.json()).then(d => {
      if (d.success && d.invite) setPendingInvite(d.invite);
    }).catch(() => {});
  }, [user]);

  async function handleApplyCoupon() {
    setCouponError('');
    if (!couponInput.trim()) { setCouponError('Enter a coupon code'); return; }
    setCouponApplying(true);
    try {
      const planPrice = currentPlansForCycle(billingCycle).find(p => p.id === selectedPlan)?.price ?? 0;
      const res = await apiFetch('/api/coupons/validate', {
        method: 'POST',
        body: JSON.stringify({ code: couponInput.trim(), planId: selectedPlan, billingCycle, amount: planPrice }),
      });
      const data = await res.json();
      if (!data.success) { setCouponError(data.error || 'Invalid coupon'); setAppliedCoupon(null); return; }
      setAppliedCoupon({ code: data.coupon.code, discountAmount: data.discountAmount, finalAmount: data.finalAmount });
    } catch {
      setCouponError('Could not validate that coupon. Please try again.');
    } finally {
      setCouponApplying(false);
    }
  }

  // A coupon is only valid for the plan/cycle it was checked against —
  // changing either invalidates whatever was applied rather than silently
  // carrying a stale discount into checkout.
  useEffect(() => {
    setAppliedCoupon(null);
    setCouponError('');
  }, [selectedPlan, billingCycle]);

  /* ── refresh user data from Firebase ── */
  const refreshUserData = useCallback(async () => {
    if (!user) return;
    const snap = await get(ref(db, `users/${user.uid}`));
    const data = snap.val() as UserData | null;
    setUserData(data);
    if (data?.plan) setSelectedPlan(data.plan);
    if (data?.paymentStatus) setPaymentStatus(data.paymentStatus);
    if (data?.failedPaymentAttempts) setFailedAttempts(data.failedPaymentAttempts);
    if (data?.lastPaymentDate) {
      const next = new Date(data.lastPaymentDate);
      next.setDate(next.getDate() + (data.billingCycle === 'yearly' ? 365 : 30));
      setNextBillingDate(next);
    }
  }, [user]);

  /* ── load invoices ── */
  const loadInvoices = useCallback(async (uid: string) => {
    const snap = await get(ref(db, `invoices/${uid}`));
    if (snap.exists()) {
      const list: Invoice[] = [];
      snap.forEach((c) => { list.push({ id: c.key!, ...c.val() } as Invoice); });
      list.sort((a, b) => (b.createdAt || b.date || 0) - (a.createdAt || a.date || 0));
      setInvoices(list);
      return list;
    }
    setInvoices([]);
    return [] as Invoice[];
  }, []);

  /* ── Paynow redirect return: auto-poll until confirmed ── */
  useEffect(() => {
    if (!user) return;
    const pending = (() => { try { return JSON.parse(localStorage.getItem('paynow_pending') || 'null'); } catch { return null; } })();
    if (!pending?.pollUrl) return;
    setPaynowVerifying(true);
    const interval = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/paynow/poll?pollUrl=${encodeURIComponent(pending.pollUrl)}&plan=${pending.plan}&billingCycle=${pending.billingCycle}&amount=${pending.amount}&reference=${pending.reference}`);
        const data = await res.json();
        if (data.paid) {
          clearInterval(interval);
          localStorage.removeItem('paynow_pending');
          setPaynowVerifying(false);
          const snap = await get(ref(db, `users/${user.uid}`));
          const ud = snap.val() as UserData | null;
          setUserData(ud);
          if (ud?.plan) setSelectedPlan(ud.plan);
          if (ud?.paymentStatus) setPaymentStatus(ud.paymentStatus);
          if (ud?.lastPaymentDate) {
            const next = new Date(ud.lastPaymentDate);
            next.setDate(next.getDate() + (ud.billingCycle === 'yearly' ? 365 : 30));
            setNextBillingDate(next);
          }
          await loadInvoices(user.uid);
          alert('🎉 Paynow payment confirmed! Your plan has been upgraded.');
        }
      } catch {}
    }, 3000);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setPaynowVerifying(false);
      localStorage.removeItem('paynow_pending');
    }, 300000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [user, loadInvoices]);

  /* ── auth + data ── */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (cu) => {
      setUser(cu);
      if (!cu) return;
      const snap = await get(ref(db, `users/${cu.uid}`));
      const data = snap.val() as UserData | null;
      setUserData(data);
      if (data?.plan) setSelectedPlan(data.plan);
      if (data?.cancelAtPeriodEnd) setCancelAtPeriodEnd(true);
      if (data?.subscriptionStatus) setSubscriptionStatus(data.subscriptionStatus);
      if (data?.lastPaymentDate) {
        const last = new Date(data.lastPaymentDate);
        const next = new Date(last);
        next.setDate(last.getDate() + (data.billingCycle === 'yearly' ? 365 : 30));
        setNextBillingDate(next);
      }
      if (data?.paymentStatus) setPaymentStatus(data.paymentStatus);
      if (data?.failedPaymentAttempts) setFailedAttempts(data.failedPaymentAttempts);

      const allInvoices = await loadInvoices(cu.uid);

      // Auto-open the invoice modal if ?invoiceId= is in URL
      if (invoiceIdFromUrl) {
        const target = allInvoices.find(inv => inv.id === invoiceIdFromUrl);
        if (target && target.status === 'unpaid') { setActiveInvoice(target); setShowInvoiceModal(true); }
      }

      const bQ = query(ref(db, 'bots'), orderByChild('userId'), equalTo(cu.uid));
      const bS = await get(bQ);
      const botList: BotRecord[] = [];
      bS.forEach((c) => { botList.push({ id: c.key!, ...c.val() }); });
      setBots(botList);
    });
    return unsub;
  }, [loadInvoices, invoiceIdFromUrl]);

  // Fetch payment method config to know if Stripe is enabled
  useEffect(() => {
    apiFetch('/api/payment-methods-config')
      .then(r => r.json())
      .then(d => setStripeEnabled(!!d.stripe))
      .catch(() => setStripeEnabled(true));
  }, []);

  /* ── derived ── */
  const currentPlans = currentPlansForCycle(billingCycle);
  const selectedPlanData = currentPlans.find(p => p.id === selectedPlan);
  const unpaidInvoices = invoices.filter(inv => inv.status === 'unpaid');

  const getPlanLabel = () => {
    const m: Record<string, string> = { free: 'Free Plan', starter: 'Starter Plan', business: 'Business Plan', agency: 'Agency Plan' };
    return m[userData?.plan ?? 'free'] ?? 'Free Plan';
  };
  const getPlanPrice = () => {
    const m: Record<string, string> = { free: '$0/month', starter: '$15/month', business: '$45/month', agency: '$120/month' };
    return m[userData?.plan ?? 'free'] ?? '$0/month';
  };

  /* ── trial helpers ── */
  const isTrialEligible = userData?.plan === 'free' && !userData?.trialUsed;
  const isOnTrial = userData?.paymentStatus === 'trial';
  const trialDaysLeft = userData?.trialEndsAt ? Math.max(0, Math.ceil((userData.trialEndsAt - Date.now()) / 86400000)) : 0;
  const trialEndStr = userData?.trialEndsAt ? new Date(userData.trialEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
  const trialPreviewEndStr = new Date(Date.now() + 7 * 86400000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const handleStartTrial = async () => {
    setTrialLoading(true);
    setTrialError('');
    try {
      // Step 1: check if Stripe is enabled and get SetupIntent if so
      const res1 = await apiFetch('/api/trial/setup-intent', { method: 'POST' });
      const siData = await res1.json();
      if (siData.error) throw new Error(siData.error);

      // ── No-card path (Stripe disabled) ───────────────────────────────────
      if (siData.stripeDisabled) {
        const res2 = await apiFetch('/api/trial/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan: 'business', billingCycle: trialBillingCycle }),
        });
        const data = await res2.json();
        if (!data.success) throw new Error(data.error || 'Trial start failed');
        setShowTrialModal(false);
        await refreshUserData();
        alert('🎉 Your 7-day Business trial has started! You will receive an invoice when the trial ends.');
        return;
      }

      // ── Card path (Stripe enabled) ────────────────────────────────────────
      const stripe = (window as any).Stripe?.(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
      if (!stripe) throw new Error('Stripe not loaded');

      // Step 2: collect card via Elements (mounted in TrialModal via ref)
      const cardEl = (window as any).__trialCardElement;
      if (!cardEl) throw new Error('Card element not ready');

      const { setupIntent, error: confirmErr } = await stripe.confirmCardSetup(siData.clientSecret, {
        payment_method: { card: cardEl, billing_details: { name: userData?.name || user?.displayName || '' } },
      });
      if (confirmErr) throw new Error(confirmErr.message);

      // Step 3: confirm trial on server with card details
      const res2 = await apiFetch('/api/trial/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentMethodId: setupIntent.payment_method,
          plan: 'business',
          billingCycle: trialBillingCycle,
          setupIntentId: setupIntent.id,
        }),
      });
      const data = await res2.json();
      if (!data.success) throw new Error(data.error || 'Trial start failed');

      setShowTrialModal(false);
      await refreshUserData();
      alert('🎉 Your 7-day Business trial has started! Enjoy full access.');
    } catch (err: any) {
      setTrialError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setTrialLoading(false);
    }
  };

  /* ── handlers ── */
  const handleLogout = async () => { await signOut(auth); navigate('/'); };

  const handleSubscriptionPaymentSuccess = async () => {
    setShowSubscriptionModal(false);
    if (!user) return;
    const snap = await get(ref(db, `users/${user.uid}`));
    const data = snap.val() as UserData | null;
    setUserData(data);
    if (data?.plan) setSelectedPlan(data.plan);
    if (data?.paymentStatus) setPaymentStatus(data.paymentStatus);
    if (data?.lastPaymentDate) {
      const next = new Date(data.lastPaymentDate);
      next.setDate(next.getDate() + (data.billingCycle === 'yearly' ? 365 : 30));
      setNextBillingDate(next);
    }
    await loadInvoices(user.uid);
    alert(cardOnlyMode ? '✅ Card saved successfully!' : '🎉 Payment successful! Your plan has been upgraded.');
    setCardOnlyMode(false);
  };

  const handleInvoicePaymentSuccess = async () => {
    if (!user || !activeInvoice) return;
    setShowInvoiceModal(false);

    // Mark invoice as paid
    const now = Date.now();
    await set(ref(db, `invoices/${user.uid}/${activeInvoice.id}/status`), 'paid');
    await set(ref(db, `invoices/${user.uid}/${activeInvoice.id}/paidAt`), now);

    // Activate the bot if it has a pending invoice
    if (activeInvoice.botId) {
      await set(ref(db, `bots/${activeInvoice.botId}/status`), 'active');
      await set(ref(db, `bots/${activeInvoice.botId}/setupFeePaid`), true);
      await set(ref(db, `bots/${activeInvoice.botId}/pendingInvoiceId`), null);
    }

    await loadInvoices(user.uid);
    setActiveInvoice(null);

    // Notify server to send payment confirmation email (fire-and-forget)
    apiFetch('/api/notify/setup-fee-paid', {
      method: 'POST',
      body: JSON.stringify({ invoiceId: activeInvoice.id }),
    }).catch(() => {});

    // Remove invoiceId from URL
    navigate('/billing', { replace: true });
    alert('✅ Payment successful! Your bot is now active.');
  };

  const openInvoiceModal = (inv: Invoice) => { setActiveInvoice(inv); setShowInvoiceModal(true); };

  /* ── PDF download — uses server-generated PDF (same as what's emailed) ── */
  const generateInvoicePDF = async (invoice: Invoice) => {
    try {
      const res = await apiFetch(`/api/invoices/${invoice.id}/pdf`);
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${invoice.invoiceNumber || invoice.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Invoice download error:', err);
      alert('Could not download invoice. Please try again.');
    }
  };

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <>
      <SEO title={`Billing & Plans | ${companyName}`} description="Manage your subscription and invoices." keywords="billing, plans, subscription" type="website" noindex />

      <div style={S.page}>
        {/* ── Sidebar ── */}
        <div style={S.sidebar(sidebarCollapsed)}>
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} style={{ position: 'absolute', right: '-13px', top: '22px', width: '26px', height: '26px', background: '#5B4FE8', border: 'none', borderRadius: '50%', color: 'white', cursor: 'pointer', zIndex: 10, fontSize: '11px' }}>
            {sidebarCollapsed ? '→' : '←'}
          </button>
          <div style={{ padding: sidebarCollapsed ? '20px 0' : '22px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
            {!sidebarCollapsed ? (<><img src={isDark ? '/Logo-white.png' : '/Logo-black.png'} alt={companyName} style={{ height: '68px', display: 'block', maxWidth: '130px', objectFit: 'contain' }} /><div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '4px' }}>Bot Platform</div></>) : <div style={{ textAlign: 'center' }}><img src={isDark ? '/Logo-white.png' : '/Logo-black.png'} alt={companyName} style={{ height: '68px', objectFit: 'contain' }} /></div>}
          </div>
          <nav style={{ flex: 1, padding: sidebarCollapsed ? '14px 0' : '16px 10px', overflowY: 'auto' }}>
            {[
              { to: '/dashboard', icon: '📊', label: 'Dashboard' },
              { to: '/dashboard', icon: '📈', label: 'Reports' },
              { to: '/dashboard', icon: '💬', label: 'Conversations' },
            ].map(({ to, icon, label }) => (
              <Link key={label} to={to} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: sidebarCollapsed ? '10px 0' : '9px 12px', borderRadius: '9px', color: 'var(--text-muted)', marginBottom: '3px', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
                  <span style={{ fontSize: '16px' }}>{icon}</span>
                  {!sidebarCollapsed && <span style={{ fontSize: '13px', fontWeight: '500' }}>{label}</span>}
                </div>
              </Link>
            ))}

            {bots.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                {!sidebarCollapsed && <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#334155', fontWeight: '600', letterSpacing: '0.06em', padding: '0 12px', marginBottom: '8px' }}>Bots</div>}
                {bots.slice(0, 3).map(bot => (
                  <div key={bot.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: sidebarCollapsed ? '10px 0' : '9px 12px', borderRadius: '9px', color: 'var(--text-muted)', marginBottom: '3px', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
                    <span style={{ fontSize: '16px' }}>🤖</span>
                    {!sidebarCollapsed && (
                      <>
                        <span style={{ fontSize: '13px' }}>{bot.businessName}</span>
                        {(bot as any).status === 'suspended' && (
                          <span style={{ marginLeft: 'auto', background: '#dc2626', color: 'white', fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '20px' }}>SUSPENDED</span>
                        )}
                      </>
                    )}
                  </div>
                ))}
                {!sidebarCollapsed && (
                  <Link to="/create" style={{ display: 'block', margin: '8px 12px 0', padding: '8px', background: 'var(--bg-element)', textAlign: 'center', borderRadius: '8px', color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '13px' }}>
                    + New Bot
                  </Link>
                )}
              </div>
            )}

            <div style={{ marginTop: '16px' }}>
              <Link to="/billing" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: sidebarCollapsed ? '10px 0' : '9px 12px', borderRadius: '9px', background: 'var(--bg-element)', color: 'var(--text-primary)', marginBottom: '3px', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
                  <span style={{ fontSize: '16px' }}>💳</span>
                  {!sidebarCollapsed && <span style={{ fontSize: '13px', fontWeight: '500' }}>Billing</span>}
                  {!sidebarCollapsed && unpaidInvoices.length > 0 && (
                    <span style={{ marginLeft: 'auto', background: '#f59e0b', color: '#1a1200', fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '20px' }}>{unpaidInvoices.length}</span>
                  )}
                </div>
              </Link>
              <Link to="/documentation" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: sidebarCollapsed ? '10px 0' : '9px 12px', borderRadius: '9px', color: 'var(--text-muted)', marginBottom: '3px', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
                  <span style={{ fontSize: '16px' }}>📚</span>
                  {!sidebarCollapsed && <span style={{ fontSize: '13px', fontWeight: '500' }}>Documentation</span>}
                </div>
              </Link>
              {/* Theme toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between', padding: sidebarCollapsed ? '10px 0' : '8px 14px', margin: '4px 0' }}>
                {!sidebarCollapsed && <span style={{ fontSize: '12px', color: 'var(--text-faint)', userSelect: 'none' }}>{isDark ? '🌙 Dark' : '☀️ Light'}</span>}
                <div onClick={toggle} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'} style={{ width: '44px', height: '24px', borderRadius: '12px', background: isDark ? 'var(--bg-element)' : '#5B4FE8', border: `1px solid ${isDark ? 'var(--border)' : '#4338ca'}`, position: 'relative', cursor: 'pointer', transition: 'background 0.25s, border-color 0.25s', flexShrink: 0 }}>
                  <div style={{ position: 'absolute', top: '3px', left: isDark ? '3px' : '21px', width: '16px', height: '16px', borderRadius: '50%', background: isDark ? 'var(--text-faint)' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', lineHeight: 1, transition: 'left 0.25s, background 0.25s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                    {isDark ? '🌙' : '☀️'}
                  </div>
                </div>
              </div>
              <div onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: sidebarCollapsed ? '10px 0' : '9px 12px', borderRadius: '9px', color: 'var(--text-muted)', cursor: 'pointer', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
                <span style={{ fontSize: '16px' }}>🚪</span>
                {!sidebarCollapsed && <span style={{ fontSize: '13px', fontWeight: '500' }}>Logout</span>}
              </div>
            </div>
          </nav>
          {!sidebarCollapsed && user && <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border-subtle)', fontSize: '11px', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>}
        </div>

        {/* ── Main ── */}
        <div style={S.main}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

            {paynowVerifying && (
              <div style={{ marginBottom: '20px', padding: '14px 20px', background: '#a855f710', border: '1px solid #a855f740', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '18px' }}>💳</span>
                <div>
                  <div style={{ color: '#c084fc', fontWeight: '700', fontSize: '14px' }}>Verifying your Paynow payment...</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '2px' }}>Please wait while we confirm your payment with Paynow. This may take a moment.</div>
                </div>
              </div>
            )}

            {pendingInvite && (
              <div style={{ marginBottom: '20px', padding: '14px 20px', background: '#05966910', border: '1px solid #05966940', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '18px' }}>🎁</span>
                  <div>
                    <div style={{ color: '#059669', fontWeight: '700', fontSize: '14px' }}>You have a coupon waiting — {pendingInvite.code}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '2px' }}>Proposed for the {pendingInvite.proposedPlan} plan</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedPlan(pendingInvite.proposedPlan);
                    setBillingCycle(pendingInvite.proposedBillingCycle === 'yearly' ? 'yearly' : 'monthly');
                    setCouponInput(pendingInvite.code);
                    setShowSubscriptionModal(true);
                  }}
                  style={{ padding: '8px 18px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}
                >
                  Apply now →
                </button>
              </div>
            )}

            <div style={{ marginBottom: '32px' }}>
              <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>Account</div>
              <h1 style={{ color: 'var(--text-primary)', fontSize: '28px', fontWeight: '700', letterSpacing: '-0.02em', margin: 0 }}>Billing & Plans</h1>
              <p style={{ color: 'var(--text-faint)', fontSize: '14px', marginTop: '6px' }}>Manage your subscription, invoices, and payment method</p>
            </div>

            {/* ── Outstanding invoices ── */}
            {unpaidInvoices.map(inv => (
              <OutstandingInvoiceBanner key={inv.id} invoice={inv} onPay={() => openInvoiceModal(inv)} />
            ))}

            {/* ── Current plan banner ── */}
            <div style={{ background: userData?.plan === 'free' || paymentStatus === 'failed' ? 'linear-gradient(135deg,var(--bg-element),#2a2d3a)' : 'linear-gradient(135deg,#2d1b69,#1a1040)', borderRadius: '18px', padding: '28px 32px', marginBottom: '28px', border: '1px solid var(--card-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>Current Plan</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '6px' }}>{getPlanLabel()}</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {getPlanPrice()} · {userData?.plan === 'free' ? 'No credit card required' : 'Auto-renews monthly'}
                  </div>
                  {paymentStatus === 'failed' && (
                    <div style={{ marginTop: '12px', background: '#dc262622', border: '1px solid #dc262640', padding: '8px 16px', borderRadius: '8px', color: '#f87171', fontSize: '13px', fontWeight: '500' }}>
                      ⚠️ Payment failed — update your payment method to restore access
                    </div>
                  )}
                </div>
                {userData?.plan !== 'free' && paymentStatus === 'active' && nextBillingDate && (
                  <div style={{ background: 'var(--border-subtle)', borderRadius: '12px', padding: '14px 24px', textAlign: 'center', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>NEXT BILLING</div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      {nextBillingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Billing cycle toggle ── */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '28px' }}>
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '40px', padding: '4px', display: 'inline-flex' }}>
                {(['monthly', 'yearly'] as const).map(cycle => (
                  <button key={cycle} onClick={() => setBillingCycle(cycle)} style={{ padding: '9px 28px', borderRadius: '36px', border: 'none', background: billingCycle === cycle ? '#5B4FE8' : 'transparent', color: billingCycle === cycle ? 'white' : 'var(--text-muted)', fontWeight: '600', cursor: 'pointer', fontSize: '14px', position: 'relative', transition: 'all 0.2s' }}>
                    {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
                    {cycle === 'yearly' && <span style={{ position: 'absolute', top: '-8px', right: '-20px', background: '#10b981', color: 'white', fontSize: '9px', padding: '2px 6px', borderRadius: '20px', fontWeight: '700' }}>-20%</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Payment method card ── */}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '16px', padding: '24px', marginBottom: '28px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                <div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '16px', marginBottom: '3px' }}>Payment Method</div>
                  <div style={{ color: 'var(--text-faint)', fontSize: '13px' }}>Your saved payment details</div>
                </div>
                <button onClick={() => { setCardOnlyMode(true); setShowSubscriptionModal(true); }} style={{ padding: '8px 18px', background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                  {userData?.stripePaymentMethodId ? '🔄 Update Card' : '+ Add Card'}
                </button>
              </div>

              {userData?.stripePaymentMethodId ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', background: 'linear-gradient(135deg,#1e293b,#0f172a)', borderRadius: '14px', maxWidth: '380px' }}>
                  <div style={{ fontSize: '36px' }}>💳</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'white', fontWeight: '700', letterSpacing: '2px', marginBottom: '4px' }}>•••• •••• •••• {userData?.cardLast4 || '****'}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', gap: '12px' }}>
                      <span>{userData?.cardBrand || 'Card'}</span>
                      <span>Expires {userData?.cardExpiry || '**/**'}</span>
                    </div>
                  </div>
                  <div style={{ background: '#10b981', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', color: 'white' }}>✓ Active</div>
                </div>
              ) : (
                <div onClick={() => { setCardOnlyMode(true); setShowSubscriptionModal(true); }} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '18px', border: '2px dashed var(--bg-element)', borderRadius: '14px', cursor: 'pointer', maxWidth: '380px' }}>
                  <div style={{ width: '44px', height: '44px', background: 'var(--bg-element)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>💳</div>
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', marginBottom: '3px', fontSize: '14px' }}>No payment method added</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Required to upgrade your plan</div>
                  </div>
                </div>
              )}

              {paymentStatus === 'failed' && (
                <div style={{ marginTop: '14px', padding: '12px 16px', background: '#dc262615', border: '1px solid #dc262630', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '18px' }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight: '600', color: '#f87171', fontSize: '13px' }}>Payment failed</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Update your card to restore your plan. {failedAttempts}/3 attempts failed.</div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Trial status banner (shown when user is on trial) ── */}
            {isOnTrial && (
              <div style={{ background: 'linear-gradient(135deg,#3d35b5,#7c3aed)', borderRadius: '14px', padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', color: '#fff', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <span style={{ fontWeight: 700, fontSize: '15px' }}>🎁 Business Plan Trial</span>
                  <span style={{ opacity: 0.85, marginLeft: '12px', fontSize: '13px' }}>
                    {trialDaysLeft > 0 ? `${trialDaysLeft} day${trialDaysLeft > 1 ? 's' : ''} remaining` : 'Trial ends today'}
                    {trialEndStr ? (userData?.stripePaymentMethodId ? ` — card charged on ${trialEndStr}` : ` — invoice sent on ${trialEndStr}`) : ''}
                  </span>
                </div>
                <button onClick={() => navigate('/billing#payment')} style={{ padding: '7px 16px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                  Manage Billing →
                </button>
              </div>
            )}

            {/* ── Plan cards ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: '16px', marginBottom: '32px' }}>
              {currentPlans.map(plan => (
                <div key={plan.id} onClick={() => setSelectedPlan(plan.id)} style={{ background: 'var(--bg-card)', borderRadius: '16px', border: selectedPlan === plan.id ? `2px solid ${plan.color}` : '1px solid var(--bg-element)', cursor: 'pointer', transition: 'all 0.2s', position: 'relative', overflow: 'hidden' }}>
                  {plan.popular && <div style={{ position: 'absolute', top: '12px', right: '12px', background: plan.color, color: 'white', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>POPULAR</div>}
                  {/* Trial ribbon on Business card for eligible users */}
                  {isTrialEligible && plan.id === 'business' && (
                    <div style={{ position: 'absolute', top: '12px', left: '12px', background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)', color: 'white', padding: '3px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px' }}>🎁 7-DAY TRIAL</div>
                  )}
                  {userData?.plan === plan.id && !isOnTrial && <div style={{ position: 'absolute', top: '12px', left: '12px', background: '#10b981', color: 'white', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>CURRENT</div>}
                  {isOnTrial && plan.id === 'business' && <div style={{ position: 'absolute', top: '12px', right: '12px', background: '#7c3aed', color: 'white', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>TRIAL ACTIVE</div>}
                  <div style={{ padding: '22px' }}>
                    <div style={{ fontSize: '36px', marginBottom: '14px' }}>{plan.icon}</div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: '700', marginBottom: '3px' }}>{plan.name}</div>
                    <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '14px' }}>{plan.description}</div>
                    <div style={{ marginBottom: '18px' }}>
                      <span style={{ color: 'var(--text-primary)', fontSize: '30px', fontWeight: '700' }}>${plan.price}</span>
                      <span style={{ color: 'var(--text-faint)', fontSize: '13px' }}>/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                    </div>
                    <button style={{ width: '100%', padding: '10px', background: selectedPlan === plan.id ? plan.color : 'var(--bg-element)', color: selectedPlan === plan.id ? 'white' : 'var(--text-muted)', border: 'none', borderRadius: '9px', fontWeight: '600', cursor: (userData?.plan === plan.id || isOnTrial) ? 'not-allowed' : 'pointer', fontSize: '13px' }} disabled={userData?.plan === plan.id || isOnTrial}>
                      {isOnTrial && plan.id === 'business' ? 'On Trial' : userData?.plan === plan.id ? 'Current Plan' : selectedPlan === plan.id ? 'Selected' : 'Select'}
                    </button>
                    {/* Trial button on Business card */}
                    {isTrialEligible && plan.id === 'business' && (
                      <button onClick={(e) => { e.stopPropagation(); setShowTrialModal(true); }} style={{ width: '100%', marginTop: '8px', padding: '10px', background: 'transparent', color: '#a78bfa', border: '1px solid #5B4FE8', borderRadius: '9px', fontWeight: '600', cursor: 'pointer', fontSize: '13px', transition: 'all 0.2s' }}>
                        🎁 Start 7-Day Free Trial
                      </button>
                    )}
                  </div>
                  <div style={{ borderTop: '1px solid var(--bg-element)', padding: '16px 22px', background: 'var(--bg-sidebar)' }}>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600', marginBottom: '10px' }}>What's included:</div>
                    {plan.features.map((f, i) => (
                      <div key={i} style={{ marginBottom: '6px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '7px', color: 'var(--text-secondary)' }}>
                        <span style={{ color: plan.color }}>✓</span> {f}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* ── Enterprise card ── */}
              {(() => {
                const ent = dynamicPlans.find(p => p.id === 'enterprise');
                if (!ent) return null;
                return (
                  <div onClick={() => setShowEnterpriseModal(true)} style={{ background: 'linear-gradient(135deg,#1a1409,#211a05)', borderRadius: '16px', border: '1px solid #f59e0b40', cursor: 'pointer', transition: 'all 0.2s', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: '12px', right: '12px', background: '#f59e0b', color: '#000', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>CUSTOM</div>
                    <div style={{ padding: '22px' }}>
                      <div style={{ fontSize: '36px', marginBottom: '14px' }}>{ent.icon}</div>
                      <div style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: '700', marginBottom: '3px' }}>{ent.name}</div>
                      <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '14px' }}>{ent.description}</div>
                      <div style={{ marginBottom: '18px' }}>
                        <span style={{ color: '#fbbf24', fontSize: '22px', fontWeight: '700' }}>Custom pricing</span>
                      </div>
                      <button style={{ width: '100%', padding: '10px', background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#000', border: 'none', borderRadius: '9px', fontWeight: '700', cursor: 'pointer', fontSize: '13px' }}>
                        Contact Sales
                      </button>
                    </div>
                    <div style={{ borderTop: '1px solid #f59e0b20', padding: '16px 22px', background: '#12100a' }}>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600', marginBottom: '10px' }}>What's included:</div>
                      {ent.features.map((f, i) => (
                        <div key={i} style={{ marginBottom: '6px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '7px', color: 'var(--text-secondary)' }}>
                          <span style={{ color: '#f59e0b' }}>✓</span> {f}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* ── Upgrade CTA ── */}
            {userData?.plan !== selectedPlan && selectedPlan !== 'free' && (
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <button onClick={() => setShowSubscriptionModal(true)} style={{ padding: '13px 32px', background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' }}>
                  Upgrade to {selectedPlanData?.name} — ${selectedPlanData?.price}/{billingCycle === 'monthly' ? 'mo' : 'yr'}
                </button>
              </div>
            )}

            {paymentStatus === 'failed' && (
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <button onClick={() => setShowSubscriptionModal(true)} style={{ padding: '13px 32px', background: 'linear-gradient(135deg,#dc2626,#b91c1c)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' }}>
                  ⚠️ Retry Failed Payment
                </button>
              </div>
            )}

            {paymentStatus === 'pending_renewal' && unpaidInvoices.length > 0 && (
              <div style={{ background: '#f59e0b0d', border: '1px solid #f59e0b40', borderRadius: '14px', padding: '18px 22px', marginBottom: '28px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                <span style={{ fontSize: '24px' }}>🧾</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fbbf24', fontWeight: '700', fontSize: '14px', marginBottom: '3px' }}>Renewal invoice outstanding</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Pay your renewal invoice to keep your plan active. Bots will be suspended if unpaid past the due date.</div>
                </div>
                <button onClick={() => { const inv = unpaidInvoices.find(i => i.type === 'subscription_renewal'); if (inv) openInvoiceModal(inv); }} style={{ padding: '9px 20px', background: '#f59e0b', color: '#000', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' }}>
                  Pay Now
                </button>
              </div>
            )}

            {(paymentStatus === 'expired' || paymentStatus === 'cancelled') && userData?.plan === 'free' && (
              <div style={{ background: '#10b9810d', border: '1px solid #10b98130', borderRadius: '14px', padding: '18px 22px', marginBottom: '28px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                <span style={{ fontSize: '24px' }}>📉</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#10b981', fontWeight: '700', fontSize: '14px', marginBottom: '3px' }}>Your subscription has ended</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>You've been moved to the Free plan. Premium bots are suspended until you resubscribe.</div>
                </div>
                <button onClick={() => setShowSubscriptionModal(true)} style={{ padding: '9px 20px', background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' }}>
                  Resubscribe
                </button>
              </div>
            )}

            {/* ── Billing history ── */}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '16px', overflow: 'hidden', marginBottom: '28px' }}>
              <div style={{ padding: '22px 24px', borderBottom: '1px solid var(--bg-element)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '16px', marginBottom: '3px' }}>Billing History</div>
                  <div style={{ color: 'var(--text-faint)', fontSize: '13px' }}>View and download all your invoices</div>
                </div>
                {unpaidInvoices.length > 0 && (
                  <div style={{ background: '#f59e0b22', color: '#f59e0b', fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px' }}>
                    {unpaidInvoices.length} outstanding
                  </div>
                )}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--bg-element)' }}>
                      {['Invoice', 'Date', 'Description', 'Amount', 'Type', 'Status', ''].map((h, i) => (
                        <th key={i} style={{ textAlign: 'left', padding: '14px 20px', fontSize: '11px', color: 'var(--text-faint)', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.length === 0 ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-faint)', fontSize: '14px' }}>No invoices yet. Your first invoice appears here after your first payment.</td></tr>
                    ) : (
                      invoices.map(inv => (
                        <tr key={inv.id} style={{ borderBottom: '1px solid var(--bg-element)' }}>
                          <td style={{ padding: '14px 20px', color: 'var(--text-primary)', fontWeight: '500', fontSize: '13px' }}>{inv.invoiceNumber}</td>
                          <td style={{ padding: '14px 20px', color: 'var(--text-muted)', fontSize: '13px' }}>{new Date(inv.createdAt || inv.date || Date.now()).toLocaleDateString()}</td>
                          <td style={{ padding: '14px 20px', color: 'var(--text-secondary)', fontSize: '13px', maxWidth: '220px' }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.description}</div>
                            {inv.businessName && <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '2px' }}>{inv.businessName}</div>}
                          </td>
                          <td style={{ padding: '14px 20px', color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px' }}>${inv.amount}</td>
                          <td style={{ padding: '14px 20px' }}>
                            <span style={{ background: inv.type === 'setup_fee' ? '#5B4FE822' : inv.type === 'subscription_renewal' ? '#f59e0b22' : '#10b98122', color: inv.type === 'setup_fee' ? '#818cf8' : inv.type === 'subscription_renewal' ? '#fbbf24' : '#6ee7b7', fontSize: '11px', fontWeight: '600', padding: '3px 9px', borderRadius: '20px' }}>
                              {inv.type === 'setup_fee' ? 'Setup' : inv.type === 'subscription_renewal' ? 'Renewal' : 'Subscription'}
                            </span>
                          </td>
                          <td style={{ padding: '14px 20px' }}>
                            {inv.status === 'paid' ? (
                              <span style={{ background: '#10b98122', color: '#6ee7b7', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>Paid</span>
                            ) : (
                              <button onClick={() => openInvoiceModal(inv)} style={{ background: '#f59e0b22', color: '#fbbf24', border: '1px solid #f59e0b33', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                                Pay Now
                              </button>
                            )}
                          </td>
                          <td style={{ padding: '14px 20px' }}>
                            <button onClick={() => generateInvoicePDF(inv)} style={{ background: 'none', border: 'none', color: '#5B4FE8', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}>↓ PDF</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Cancel / reactivate ── */}
            {userData?.plan !== 'free' && paymentStatus === 'active' && !cancelAtPeriodEnd && (
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <button
                  onClick={async () => {
                    if (!window.confirm('Are you sure you want to cancel? You will keep access until the end of your billing period.')) return;
                    const res = await apiFetch('/api/cancel-subscription', { method: 'POST', body: JSON.stringify({}) });
                    const data = await res.json();
                    if (data.success) { alert(`Subscription cancelled. Access ends ${new Date(data.endDate).toLocaleDateString()}`); window.location.reload(); }
                    else alert('Failed to cancel: ' + data.error);
                  }}
                  style={{ padding: '10px 24px', background: 'transparent', color: 'var(--text-faint)', border: '1px solid var(--card-border)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
                >
                  Cancel Subscription
                </button>
              </div>
            )}

            {cancelAtPeriodEnd && (
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <button
                  onClick={async () => {
                    if (!window.confirm('Reactivate your subscription?')) return;
                    const res = await apiFetch('/api/reactivate-subscription', { method: 'POST', body: JSON.stringify({}) });
                    const data = await res.json();
                    if (data.success) { alert('Subscription reactivated!'); window.location.reload(); }
                    else alert('Failed to reactivate: ' + data.error);
                  }}
                  style={{ padding: '10px 24px', background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                >
                  🔄 Reactivate Subscription
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Trial modal ── */}
      {showTrialModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid #2d2050', borderRadius: '20px', padding: '32px', maxWidth: '460px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <div style={{ color: 'var(--text-primary)', fontWeight: '700', fontSize: '18px' }}>🎁 Start Your 7-Day Business Trial</div>
              <button onClick={() => { setShowTrialModal(false); setTrialError(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '22px' }}>
              {stripeEnabled
                ? 'Full Business plan access, free for 7 days. Card required — you won\'t be charged until the trial ends.'
                : 'Full Business plan access, free for 7 days. No card required — an invoice will be sent when your trial ends.'}
            </div>

            {/* Billing cycle toggle */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>BILLING CYCLE AFTER TRIAL</div>
              <div style={{ display: 'flex', background: 'var(--bg-page)', borderRadius: '10px', padding: '4px', gap: '4px' }}>
                {(['monthly', 'yearly'] as const).map(cycle => (
                  <button key={cycle} onClick={() => setTrialBillingCycle(cycle)} style={{ flex: 1, padding: '9px', borderRadius: '7px', border: 'none', background: trialBillingCycle === cycle ? '#5B4FE8' : 'transparent', color: trialBillingCycle === cycle ? 'white' : 'var(--text-muted)', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}>
                    {cycle === 'monthly' ? 'Monthly' : 'Yearly (save 20%)'}
                  </button>
                ))}
              </div>
            </div>

            {/* Pricing summary */}
            <div style={{ background: 'var(--bg-page)', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#a78bfa', fontSize: '13px', fontWeight: '600' }}>
                  {stripeEnabled ? 'After trial ends (auto-charged)' : 'After trial ends (invoice sent)'}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '2px' }}>
                  {trialBillingCycle === 'monthly' ? '$45/month' : '$432/year (~$36/mo)'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#10b981', fontWeight: '700', fontSize: '16px' }}>Free</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>for 7 days</div>
              </div>
            </div>

            {/* Stripe card element — only shown when Stripe is enabled */}
            {stripeEnabled && (
              <div style={{ marginBottom: '18px' }}>
                <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>CARD DETAILS</div>
                <TrialCardElement />
              </div>
            )}

            {/* No-card info box */}
            {!stripeEnabled && (
              <div style={{ background: 'var(--bg-page)', borderRadius: '10px', padding: '12px 16px', marginBottom: '18px', fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                📄 When your 7-day trial ends, we'll email you an invoice. Pay within 7 days to keep your subscription active.
              </div>
            )}

            {/* Legal line */}
            <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginBottom: '18px', lineHeight: '1.5' }}>
              {stripeEnabled
                ? `By starting your trial you agree that your card will be charged ${trialBillingCycle === 'monthly' ? '$45' : '$432'} on ${trialPreviewEndStr} unless you cancel before then. Cancel any time from your billing page.`
                : `Your trial starts today and ends on ${trialPreviewEndStr}. An invoice will be sent on that date. Failure to pay within 7 days will result in suspension.`}
            </div>

            {trialError && (
              <div style={{ background: '#7f1d1d20', border: '1px solid #ef444440', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', color: '#fca5a5', fontSize: '13px' }}>
                ⚠️ {trialError}
              </div>
            )}

            <button onClick={handleStartTrial} disabled={trialLoading} style={{ width: '100%', padding: '13px', background: trialLoading ? '#374151' : 'linear-gradient(135deg,#5B4FE8,#7c3aed)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: trialLoading ? 'not-allowed' : 'pointer', fontSize: '15px', boxShadow: trialLoading ? 'none' : '0 4px 20px rgba(91,79,232,0.4)' }}>
              {trialLoading ? 'Starting trial...' : 'Start Free Trial →'}
            </button>
            <button onClick={() => { setShowTrialModal(false); setTrialError(''); }} style={{ width: '100%', marginTop: '10px', padding: '11px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--card-border)', borderRadius: '9px', cursor: 'pointer', fontSize: '14px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Subscription payment modal ── */}
      {showSubscriptionModal && user && selectedPlanData && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '20px', padding: '32px', maxWidth: '520px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <div style={{ color: 'var(--text-primary)', fontWeight: '700', fontSize: '18px' }}>
                {cardOnlyMode ? '💳 Save Payment Method' : 'Complete Payment'}
              </div>
              <button onClick={() => { setShowSubscriptionModal(false); setCardOnlyMode(false); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '24px' }}>
              {cardOnlyMode ? 'Save your card for future upgrades — no charge today' : `${selectedPlanData.name} Plan — $${selectedPlanData.price}/${billingCycle === 'monthly' ? 'month' : 'year'}`}
            </div>

            {!cardOnlyMode && (
              <div style={{ marginBottom: '20px', padding: '14px', background: 'var(--bg-element)', borderRadius: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px' }}>
                  Have a coupon? <span style={{ fontWeight: 400 }}>(applies to Paynow payments only)</span>
                </div>
                {appliedCoupon ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#059669', fontSize: '13px', fontWeight: 600 }}>
                      ✓ {appliedCoupon.code} applied — ${appliedCoupon.discountAmount} off → ${appliedCoupon.finalAmount}
                    </span>
                    <button onClick={() => { setAppliedCoupon(null); setCouponInput(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}>Remove</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="COUPON CODE"
                      style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '13px' }}
                    />
                    <button onClick={handleApplyCoupon} disabled={couponApplying} style={{ padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                      {couponApplying ? 'Checking…' : 'Apply'}
                    </button>
                  </div>
                )}
                {couponError && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px' }}>{couponError}</p>}
              </div>
            )}

            <PaymentGateway
              amount={cardOnlyMode ? 0 : selectedPlanData.price}
              plan={cardOnlyMode ? 'free' : selectedPlan}
              billingCycle={billingCycle}
              email={user.email}
              phone={userData?.phone}
              userId={user.uid}
              savedCardLast4={userData?.cardLast4}
              savedCardBrand={userData?.cardBrand}
              savedCardExpiry={userData?.cardExpiry}
              stripePaymentMethodId={userData?.stripePaymentMethodId}
              stripeCustomerId={userData?.stripeCustomerId}
              couponCode={appliedCoupon?.code}
              paynowAmount={appliedCoupon ? appliedCoupon.finalAmount : undefined}
              onSuccess={handleSubscriptionPaymentSuccess}
              onError={(err: string) => alert('Payment failed: ' + err)}
            />
            <button onClick={() => { setShowSubscriptionModal(false); setCardOnlyMode(false); }} style={{ width: '100%', marginTop: '12px', padding: '11px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--card-border)', borderRadius: '9px', cursor: 'pointer', fontWeight: '500', fontSize: '14px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Invoice payment modal ── */}
      {showInvoiceModal && activeInvoice && user && (
        <InvoicePayModal
          invoice={activeInvoice}
          user={user}
          userData={userData}
          onSuccess={handleInvoicePaymentSuccess}
          onClose={() => { setShowInvoiceModal(false); navigate('/billing', { replace: true }); }}
        />
      )}

      {/* ── Enterprise enquiry modal ── */}
      {showEnterpriseModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '36px', maxWidth: '480px', width: '100%', border: '1px solid #f59e0b30', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <div style={{ color: '#fbbf24', fontSize: '22px', fontWeight: '700' }}>🏗️ Enterprise Plan</div>
                <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginTop: '3px' }}>Tell us about your needs — we'll get back within 1 business day</div>
              </div>
              <button onClick={() => { setShowEnterpriseModal(false); setEntSent(false); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '20px' }}>✕</button>
            </div>

            {entSent ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                <div style={{ color: 'var(--text-primary)', fontWeight: '700', fontSize: '18px', marginBottom: '8px' }}>Enquiry sent!</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Our sales team will contact you at <strong style={{ color: 'var(--text-secondary)' }}>{entForm.email}</strong> within 1 business day.</div>
              </div>
            ) : (
              <>
                {[
                  { label: 'Your Name *', key: 'name', type: 'text', placeholder: 'Jane Doe' },
                  { label: 'Email *', key: 'email', type: 'email', placeholder: 'jane@company.com' },
                  { label: 'Company', key: 'company', type: 'text', placeholder: 'Acme Ltd' },
                  { label: 'Team Size', key: 'size', type: 'text', placeholder: 'e.g. 20–50 people' },
                ].map(({ label, key, type, placeholder }) => (
                  <div key={key} style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>{label}</label>
                    <input type={type} value={(entForm as any)[key]} onChange={e => setEntForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
                      style={{ width: '100%', padding: '10px 14px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '9px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                ))}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>What do you need?</label>
                  <textarea value={entForm.message} onChange={e => setEntForm(f => ({ ...f, message: e.target.value }))} rows={3} placeholder="Describe your use case, volume, or special requirements…"
                    style={{ width: '100%', padding: '10px 14px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '9px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
                <button disabled={entLoading || !entForm.name || !entForm.email} onClick={async () => {
                  setEntLoading(true);
                  try {
                    await fetch(`${API_BASE}/api/enterprise-enquiry`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(entForm),
                    });
                    setEntSent(true);
                  } finally { setEntLoading(false); }
                }} style={{ width: '100%', padding: '12px', background: entLoading || !entForm.name || !entForm.email ? 'var(--bg-element)' : 'linear-gradient(135deg,#f59e0b,#d97706)', color: entLoading || !entForm.name || !entForm.email ? 'var(--text-muted)' : '#000', border: 'none', borderRadius: '9px', fontWeight: '700', cursor: entLoading || !entForm.name || !entForm.email ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
                  {entLoading ? 'Sending…' : 'Send Enquiry'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}